import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import { oTieuDeCot, AN_DUOI_MD, chuXuongDong } from "lib/kieuBangDuLieu";
import {
  useDialogFullManHinh,
  oDialogContent,
  oDialogActions,
  oDialogContentFormDai,
  oDialogActionsFormDai,
  oDialogPaperFormDai,
  oDialogFormFlex,
} from "lib/dungDialogFullManHinh";
import { useAuth } from "context/AuthContext";
import {
  yeuCauSuaChuVuonVuonApi,
  FIELD_CHO_PHEP_CHU_VUON,
  FIELD_CHO_PHEP_VUON,
} from "api/yeuCauSuaChuVuonVuon";
import { NHAN_LOAI_CHU_VUON } from "lib/quyTacMaChuVuon";
import { LAYOUT, SEMANTIC } from "assets/theme/avocadoBrand";

const NHAN_TRANG_THAI = {
  cho_duyet: { label: "Chờ duyệt", color: "warning" },
  da_duyet: { label: "Đã duyệt", color: "success" },
  tu_choi: { label: "Từ chối", color: "error" },
};

const NHAN_FIELD_CHU_VUON = {
  hoTen: "Họ tên",
  ngaySinh: "Ngày sinh",
  sdt: "SĐT",
  loaiChuVuon: "Loại chủ vườn",
  ngayCapCccd: "Ngày cấp CCCD",
  noiCapCccd: "Nơi cấp CCCD",
  email: "Email",
  diaChi: "Địa chỉ",
  tinh: "Tỉnh",
  tinhMa: "Mã tỉnh",
  xa: "Xã",
  xaMa: "Mã xã",
  thon: "Thôn",
  soTkNganHang: "Số TK ngân hàng",
  tenNganHang: "Ngân hàng",
  chuTaiKhoan: "Chủ tài khoản",
  ghiChu: "Ghi chú",
};

const NHAN_FIELD_VUON = {
  tenVuon: "Tên vườn",
  tinh: "Tỉnh",
  xa: "Xã",
  thon: "Thôn",
  diaChiChiTiet: "Địa chỉ chi tiết",
  dienTichHa: "Diện tích (ha)",
  namBatDauTrong: "Năm bắt đầu trồng",
  soCay: "Số cây",
  giongBo: "Giống bơ",
  maSoVungTrong: "Mã số vùng trồng",
  chungNhan: "Chứng nhận",
  ghiChu: "Ghi chú",
};

function nhanTrangThai(tt) {
  return NHAN_TRANG_THAI[tt] ?? { label: tt ?? "—", color: "secondary" };
}

function nhanLoai(loai) {
  return loai === "chuVuon" ? "Chủ vườn" : loai === "vuon" ? "Vườn" : loai ?? "—";
}

function formatGiaTri(field, giaTri) {
  if (giaTri == null || giaTri === "") return "—";
  if (field === "loaiChuVuon") return NHAN_LOAI_CHU_VUON[giaTri] ?? String(giaTri);
  if (Array.isArray(giaTri)) return giaTri.length ? giaTri.join(", ") : "—";
  if (typeof giaTri === "object") return JSON.stringify(giaTri);
  return String(giaTri);
}

function bangGiaTri(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function thoiGianVN(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("vi-VN");
}

function ThongTinPhu({ phan }) {
  const con = phan.filter(Boolean);
  if (con.length === 0) return null;
  return (
    <MDTypography
      variant="caption"
      color="text"
      display={{ xs: "block", md: "none" }}
      mt={0.25}
      lineHeight={1.4}
      sx={chuXuongDong}
    >
      {con.join(" · ")}
    </MDTypography>
  );
}

ThongTinPhu.propTypes = {
  phan: PropTypes.arrayOf(PropTypes.string).isRequired,
};

function DongDiff({ nhan, cu, moi, doi }) {
  return (
    <MDBox
      py={1.25}
      sx={{
        borderBottom: `1px solid ${SEMANTIC.border}`,
        backgroundColor: doi ? "rgba(210, 162, 58, 0.08)" : "transparent",
        px: { xs: 1, sm: 1.5 },
        borderRadius: doi ? 1 : 0,
      }}
    >
      <MDTypography variant="overline" color="text" display="block" lineHeight={1.2}>
        {nhan}
        {doi ? " · có thay đổi" : ""}
      </MDTypography>
      <Grid container spacing={1} mt={0.25}>
        <Grid item xs={12} sm={6}>
          <MDTypography variant="caption" color="text" display="block">
            Cũ
          </MDTypography>
          <MDTypography
            variant="button"
            fontWeight={doi ? "medium" : "regular"}
            display="block"
            sx={chuXuongDong}
          >
            {cu}
          </MDTypography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <MDTypography variant="caption" color="text" display="block">
            Mới
          </MDTypography>
          <MDTypography
            variant="button"
            fontWeight={doi ? "bold" : "regular"}
            color={doi ? "info" : "dark"}
            display="block"
            sx={chuXuongDong}
          >
            {moi}
          </MDTypography>
        </Grid>
      </Grid>
    </MDBox>
  );
}

DongDiff.propTypes = {
  nhan: PropTypes.string.isRequired,
  cu: PropTypes.string.isRequired,
  moi: PropTypes.string.isRequired,
  doi: PropTypes.bool.isRequired,
};

function ChipTrangThai({ trangThai }) {
  const tt = nhanTrangThai(trangThai);
  return <Chip size="small" label={tt.label} color={tt.color} variant="outlined" />;
}

ChipTrangThai.propTypes = {
  trangThai: PropTypes.string,
};

ChipTrangThai.defaultProps = {
  trangThai: null,
};

function ChoDuyetChuVuonVuon() {
  const { vaiTro } = useAuth();
  const laAdmin = vaiTro === "admin";
  const fullManHinh = useDialogFullManHinh();

  const [danhSach, setDanhSach] = useState([]);
  const [dangTai, setDangTai] = useState(true);
  const [loi, setLoi] = useState("");
  const [thongBao, setThongBao] = useState("");

  // Admin: "cho_duyet" | "tat_ca". NV luôn xem yêu cầu của mình (mọi trạng thái).
  const [boLoc, setBoLoc] = useState("cho_duyet");

  const [xemChiTiet, setXemChiTiet] = useState(null);
  const [modalTuChoi, setModalTuChoi] = useState(null);
  const [lyDoTuChoi, setLyDoTuChoi] = useState("");
  const [dangXuLy, setDangXuLy] = useState(false);

  useEffect(() => {
    setDangTai(true);
    setLoi("");
    let huy;
    const onChange = (ds) => {
      setDanhSach(ds ?? []);
      setDangTai(false);
    };
    const onError = (err) => {
      setLoi(err?.message || "Không tải được danh sách yêu cầu.");
      setDangTai(false);
    };

    if (!laAdmin) {
      huy = yeuCauSuaChuVuonVuonApi.langNgheCuaToi(onChange, onError);
    } else if (boLoc === "cho_duyet") {
      huy = yeuCauSuaChuVuonVuonApi.langNgheChoDuyet(onChange, onError);
    } else {
      huy = yeuCauSuaChuVuonVuonApi.langNgheDanhSachTatCa(onChange, onError);
    }
    return () => {
      if (typeof huy === "function") huy();
    };
  }, [laAdmin, boLoc]);

  const soChoDuyet = useMemo(
    () => danhSach.filter((r) => r.trangThai === "cho_duyet").length,
    [danhSach]
  );

  function dongDiffCua(yc) {
    const nhanMap = yc.loai === "chuVuon" ? NHAN_FIELD_CHU_VUON : NHAN_FIELD_VUON;
    const whitelist = yc.loai === "chuVuon" ? FIELD_CHO_PHEP_CHU_VUON : FIELD_CHO_PHEP_VUON;
    const keys = [
      ...new Set([
        ...Object.keys(yc.duLieuMoi ?? {}),
        ...Object.keys(yc.duLieuCu ?? {}),
        ...whitelist.filter((k) => (yc.duLieuMoi ?? {})[k] !== undefined),
      ]),
    ].filter((k) => nhanMap[k]);

    return keys.map((k) => {
      const cu = yc.duLieuCu?.[k];
      const moi = yc.duLieuMoi?.[k];
      return {
        key: k,
        nhan: nhanMap[k] ?? k,
        cu: formatGiaTri(k, cu),
        moi: formatGiaTri(k, moi),
        doi: !bangGiaTri(cu, moi),
      };
    });
  }

  async function xuLyDuyet(yc) {
    // eslint-disable-next-line no-alert
    if (
      !window.confirm(`Duyệt yêu cầu sửa ${nhanLoai(yc.loai)} “${yc.tenTarget ?? yc.targetId}”?`)
    ) {
      return;
    }
    setDangXuLy(true);
    setLoi("");
    setThongBao("");
    try {
      await yeuCauSuaChuVuonVuonApi.duyet(yc.id);
      setThongBao("Đã duyệt và áp dụng thay đổi.");
      setXemChiTiet(null);
    } catch (err) {
      setLoi(err.message || "Không duyệt được yêu cầu.");
    } finally {
      setDangXuLy(false);
    }
  }

  async function xuLyTuChoi(e) {
    e.preventDefault();
    if (!modalTuChoi) return;
    setDangXuLy(true);
    setLoi("");
    setThongBao("");
    try {
      await yeuCauSuaChuVuonVuonApi.tuChoi(modalTuChoi.id, { lyDoTuChoi });
      setThongBao("Đã từ chối yêu cầu.");
      setModalTuChoi(null);
      setLyDoTuChoi("");
      setXemChiTiet(null);
    } catch (err) {
      setLoi(err.message || "Không từ chối được yêu cầu.");
    } finally {
      setDangXuLy(false);
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan={laAdmin ? "Duyệt hồ sơ" : "Yêu cầu của tôi"}
          tieuDe="Chờ duyệt CV/Vườn"
          moTa={
            laAdmin
              ? "Duyệt hoặc từ chối đề xuất sửa Chủ vườn / Vườn từ nhân viên hiện trường."
              : "Theo dõi trạng thái các yêu cầu chỉnh sửa Chủ vườn / Vườn bạn đã gửi."
          }
        />

        {loi && (
          <MDBox mb={2}>
            <MDAlert color="error">{loi}</MDAlert>
          </MDBox>
        )}
        {thongBao && (
          <MDBox mb={2}>
            <MDAlert color="success">{thongBao}</MDAlert>
          </MDBox>
        )}

        {laAdmin && (
          <MDBox mb={2} display="flex" flexWrap="wrap" alignItems="flex-end" gap={1.5}>
            <MDBox>
              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                Bộ lọc
              </MDTypography>
              <MDInput
                select
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
                value={boLoc}
                onChange={(e) => setBoLoc(e.target.value)}
                sx={{ width: { xs: "100%", sm: LAYOUT.filterSelectWidth } }}
              >
                <option value="cho_duyet">Chờ duyệt</option>
                <option value="tat_ca">Tất cả</option>
              </MDInput>
            </MDBox>
            <MDTypography variant="caption" color="text" sx={{ pb: 1 }}>
              {boLoc === "cho_duyet"
                ? `${danhSach.length} yêu cầu chờ duyệt`
                : `${soChoDuyet} chờ duyệt · ${danhSach.length} tổng`}
            </MDTypography>
          </MDBox>
        )}

        <Card>
          {!laAdmin && (
            <MDBox px={2.5} pt={2} pb={0}>
              <MDTypography variant="caption" color="text">
                {danhSach.length} yêu cầu của bạn
                {soChoDuyet > 0 ? ` · ${soChoDuyet} đang chờ duyệt` : ""}
              </MDTypography>
            </MDBox>
          )}

          {dangTai ? (
            <MDBox px={2.5} py={3}>
              <MDTypography variant="button" color="text">
                Đang tải...
              </MDTypography>
            </MDBox>
          ) : danhSach.length === 0 ? (
            <MDBox px={2.5} pb={4} pt={2} textAlign="center">
              <Icon sx={({ palette }) => ({ color: palette.grey[400], fontSize: "2.5rem" })}>
                fact_check
              </Icon>
              <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                {laAdmin && boLoc === "cho_duyet"
                  ? "Không có yêu cầu chờ duyệt."
                  : "Chưa có yêu cầu nào."}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                {laAdmin
                  ? "Khi nhân viên gửi sửa từ trang Chủ vườn / Vườn, yêu cầu xuất hiện tại đây."
                  : "Mở Chủ vườn / Vườn → Sửa → Gửi duyệt để tạo yêu cầu mới."}
              </MDTypography>
            </MDBox>
          ) : (
            <MDBox sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={oTieuDeCot}>Thời gian</TableCell>
                    <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Loại</TableCell>
                    <TableCell sx={oTieuDeCot}>Đối tượng</TableCell>
                    {laAdmin && <TableCell sx={[oTieuDeCot, AN_DUOI_MD]}>Người gửi</TableCell>}
                    <TableCell sx={oTieuDeCot}>Trạng thái</TableCell>
                    <TableCell align="right" sx={oTieuDeCot}>
                      Thao tác
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {danhSach.map((yc) => {
                    const tt = nhanTrangThai(yc.trangThai);
                    return (
                      <TableRow
                        key={yc.id}
                        sx={({ palette }) => ({
                          transition: "background-color 160ms ease",
                          "&:hover": { backgroundColor: palette.grey[100] },
                        })}
                      >
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <MDTypography variant="button" fontWeight="medium" display="block">
                            {thoiGianVN(yc.thoiGianGui)}
                          </MDTypography>
                          <ThongTinPhu
                            phan={[nhanLoai(yc.loai), laAdmin ? yc.tenNguoiGui : null, tt.label]}
                          />
                        </TableCell>
                        <TableCell sx={AN_DUOI_MD}>{nhanLoai(yc.loai)}</TableCell>
                        <TableCell sx={chuXuongDong}>
                          <MDTypography variant="button" fontWeight="medium" display="block">
                            {yc.tenTarget || yc.targetId || "—"}
                          </MDTypography>
                          <MDTypography variant="caption" color="text" display="block">
                            {yc.targetId}
                          </MDTypography>
                        </TableCell>
                        {laAdmin && (
                          <TableCell sx={[AN_DUOI_MD, chuXuongDong]}>
                            {yc.tenNguoiGui || "—"}
                          </TableCell>
                        )}
                        <TableCell>
                          <ChipTrangThai trangThai={yc.trangThai} />
                        </TableCell>
                        <TableCell align="right">
                          <MDBox display="flex" flexWrap="wrap" justifyContent="flex-end" gap={0.5}>
                            <MDButton
                              variant="text"
                              color="dark"
                              size="small"
                              onClick={() => setXemChiTiet(yc)}
                            >
                              Xem
                            </MDButton>
                            {laAdmin && yc.trangThai === "cho_duyet" && (
                              <>
                                <MDButton
                                  variant="text"
                                  color="info"
                                  size="small"
                                  disabled={dangXuLy}
                                  onClick={() => xuLyDuyet(yc)}
                                >
                                  Duyệt
                                </MDButton>
                                <MDButton
                                  variant="text"
                                  color="error"
                                  size="small"
                                  disabled={dangXuLy}
                                  onClick={() => {
                                    setModalTuChoi(yc);
                                    setLyDoTuChoi("");
                                  }}
                                >
                                  Từ chối
                                </MDButton>
                              </>
                            )}
                          </MDBox>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </MDBox>
          )}
        </Card>
      </MDBox>
      <Footer />

      {/* ---- Dialog xem diff ---- */}
      <Dialog
        open={!!xemChiTiet}
        onClose={() => setXemChiTiet(null)}
        fullWidth
        maxWidth="md"
        fullScreen={fullManHinh}
        PaperProps={oDialogPaperFormDai}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, pr: 2, flexShrink: 0, ...chuXuongDong }}>
          <NhanDau>{nhanLoai(xemChiTiet?.loai)}</NhanDau>
          <MDTypography variant="h5" component="h2" sx={chuXuongDong}>
            {xemChiTiet?.tenTarget || xemChiTiet?.targetId || "Chi tiết yêu cầu"}
          </MDTypography>
          {xemChiTiet && (
            <MDBox mt={1} display="flex" flexWrap="wrap" gap={1} alignItems="center">
              <ChipTrangThai trangThai={xemChiTiet.trangThai} />
              <MDTypography variant="caption" color="text">
                Gửi {thoiGianVN(xemChiTiet.thoiGianGui)}
                {laAdmin && xemChiTiet.tenNguoiGui ? ` · ${xemChiTiet.tenNguoiGui}` : ""}
              </MDTypography>
            </MDBox>
          )}
        </DialogTitle>
        {xemChiTiet && (
          <MDBox sx={oDialogFormFlex}>
            <DialogContent sx={oDialogContentFormDai}>
              {xemChiTiet.trangThai === "tu_choi" && xemChiTiet.lyDoTuChoi && (
                <MDBox mb={2}>
                  <MDAlert color="error">Lý do từ chối: {xemChiTiet.lyDoTuChoi}</MDAlert>
                </MDBox>
              )}
              {dongDiffCua(xemChiTiet).length === 0 ? (
                <MDTypography variant="button" color="text">
                  Không có field nào để so sánh.
                </MDTypography>
              ) : (
                dongDiffCua(xemChiTiet).map((d) => (
                  <DongDiff key={d.key} nhan={d.nhan} cu={d.cu} moi={d.moi} doi={d.doi} />
                ))
              )}
            </DialogContent>
            <DialogActions sx={oDialogActionsFormDai}>
              <MDButton variant="outlined" color="secondary" onClick={() => setXemChiTiet(null)}>
                Đóng
              </MDButton>
              {laAdmin && xemChiTiet.trangThai === "cho_duyet" && (
                <>
                  <MDButton
                    variant="outlined"
                    color="error"
                    disabled={dangXuLy}
                    onClick={() => {
                      setModalTuChoi(xemChiTiet);
                      setLyDoTuChoi("");
                    }}
                  >
                    Từ chối
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    disabled={dangXuLy}
                    onClick={() => xuLyDuyet(xemChiTiet)}
                  >
                    {dangXuLy ? "Đang xử lý..." : "Duyệt"}
                  </MDButton>
                </>
              )}
            </DialogActions>
          </MDBox>
        )}
      </Dialog>

      {/* ---- Dialog từ chối ---- */}
      <Dialog
        open={!!modalTuChoi}
        onClose={() => !dangXuLy && setModalTuChoi(null)}
        fullWidth
        maxWidth="xs"
        fullScreen={fullManHinh}
      >
        <DialogTitle component="div" sx={{ pb: 0.5, ...chuXuongDong }}>
          <NhanDau>Từ chối</NhanDau>
          <MDTypography variant="h5" component="h2">
            Từ chối yêu cầu
          </MDTypography>
        </DialogTitle>
        <MDBox component="form" onSubmit={xuLyTuChoi}>
          <DialogContent sx={oDialogContent}>
            <MDTypography variant="button" color="text" display="block" mb={1.5}>
              {modalTuChoi?.tenTarget || modalTuChoi?.targetId} — lý do từ chối (tuỳ chọn).
            </MDTypography>
            <MDInput
              label="Lý do từ chối"
              multiline
              rows={3}
              fullWidth
              value={lyDoTuChoi}
              onChange={(e) => setLyDoTuChoi(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions sx={oDialogActions}>
            <MDButton
              variant="outlined"
              color="secondary"
              disabled={dangXuLy}
              onClick={() => setModalTuChoi(null)}
            >
              Huỷ
            </MDButton>
            <MDButton type="submit" variant="gradient" color="error" disabled={dangXuLy}>
              {dangXuLy ? "Đang xử lý..." : "Từ chối"}
            </MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>
    </DashboardLayout>
  );
}

export default ChoDuyetChuVuonVuon;
